import { useState } from 'react';
import { Copy, X } from 'lucide-react';
import { addDays, dayLabel } from '../stats/statsData';
import { FONT_MONO } from '../theme';
import { Button } from '../ui';

function relative(date, today) {
  if (date === today) return 'today';
  if (date === addDays(today, -1)) return 'yesterday';
  if (date === addDays(today, 1)) return 'tomorrow';
  return '';
}

/* Pick a recent day to copy from: one meal group (`group` set) or the whole
 * day. `days` comes from recentMealDays. A whole day copied onto a day that
 * already has food asks Append or Replace first, since a blind copy would
 * double the day; a single meal just appends. */
export function CopyMealSheet({ t, title, days, today, targetText, targetCount = 0, wholeDay = false, onCopy, onClose }) {
  const [choice, setChoice] = useState(null); // date waiting for Append/Replace
  const ask = wholeDay && targetCount > 0;

  const pickDay = (date) => {
    if (ask) { setChoice(date); return; }
    onCopy(date, 'append');
    onClose();
  };
  const finish = (mode) => { onCopy(choice, mode); onClose(); };
  const chosen = choice && days.find(d => d.date === choice);

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 100, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: t.cardBg, borderTopLeftRadius: 16, borderTopRightRadius: 16, width: '100%', maxWidth: 560, maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 -8px 32px rgba(0,0,0,0.30)' }}>
        <div style={{ padding: '10px 10px 10px 18px', borderBottom: `1px solid ${t.divider}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: t.text, display: 'flex', alignItems: 'center', gap: 8 }}>
              <Copy size={16} /> {title}
            </div>
            <div style={{ fontSize: 11, color: t.textFaint, marginTop: 2 }}>Into {targetText}, with the same amounts</div>
          </div>
          <button onClick={onClose} aria-label="Close" style={{ flexShrink: 0, width: 44, height: 40, background: 'transparent', border: 'none', cursor: 'pointer', color: t.textFaint, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <X size={18} />
          </button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto' }}>
          {days.length === 0 ? (
            <div style={{ padding: '28px 24px', textAlign: 'center', color: t.textMuted, fontSize: 13, lineHeight: 1.5 }}>
              Nothing logged {wholeDay ? 'on other days' : 'in this meal on other days'} yet.
            </div>
          ) : days.map(d => {
            const rel = relative(d.date, today);
            const more = d.count - d.names.length;
            const on = d.date === choice;
            return (
              <button key={d.date} onClick={() => pickDay(d.date)} aria-pressed={ask ? on : undefined} style={{
                width: '100%', minHeight: 56, textAlign: 'left', padding: '10px 18px', cursor: 'pointer',
                background: on ? t.accentSoft : 'transparent', border: 'none', borderBottom: `1px solid ${t.divider}`,
                display: 'flex', alignItems: 'center', gap: 12,
              }}>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: 'block', fontSize: 14, fontWeight: 600, color: on ? t.accent : t.text }}>
                    {dayLabel(d.date)}{rel && <span style={{ fontWeight: 500, color: t.textFaint }}> · {rel}</span>}
                  </span>
                  <span style={{ display: 'block', fontSize: 12, color: t.textMuted, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {d.names.join(', ')}{more > 0 ? ` +${more}` : ''}
                  </span>
                </span>
                <span style={{ flexShrink: 0, textAlign: 'right', fontFamily: FONT_MONO, fontSize: 12, color: t.textMuted, lineHeight: 1.4 }}>
                  <span style={{ display: 'block', color: t.text, fontWeight: 600 }}>{Math.round(d.kcal)} kcal</span>
                  <span style={{ display: 'block' }}>{Math.round(d.protein)} P</span>
                </span>
              </button>
            );
          })}
        </div>

        {ask && (
          <div style={{ borderTop: `1px solid ${t.divider}`, padding: '12px 18px', paddingBottom: 'max(12px, env(safe-area-inset-bottom))' }}>
            <div style={{ fontSize: 12, color: t.textMuted, lineHeight: 1.5, marginBottom: chosen ? 10 : 0 }}>
              {chosen
                ? <>Copy {chosen.count} item{chosen.count === 1 ? '' : 's'} from {dayLabel(chosen.date)}. {targetText.charAt(0).toUpperCase() + targetText.slice(1)} already has {targetCount}.</>
                : <>{targetText.charAt(0).toUpperCase() + targetText.slice(1)} already has {targetCount} item{targetCount === 1 ? '' : 's'}. Pick a day, then add to them or replace them.</>}
            </div>
            {chosen && (
              <div style={{ display: 'flex', gap: 8 }}>
                <Button t={t} variant="primary" onClick={() => finish('append')} style={{ flex: 1, justifyContent: 'center', minHeight: 40 }}>
                  Append
                </Button>
                <Button t={t} variant="danger" onClick={() => finish('replace')} style={{ flex: 1, justifyContent: 'center', minHeight: 40 }}>
                  Replace
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
